import { CustomAlert } from "@/components/ui/Alert";
import { HTTPError } from "ky";
import { ErrorInfo } from "react";
import { ZodError } from "zod";
import * as Sentry from "@sentry/react";

export const getErrorMessage = async (error: unknown): Promise<string> => {
  if (error instanceof ZodError) {
    return error.message;
  }

  if (error instanceof HTTPError) {
    const response = await error.response;
    if (response.status === 500) {
      return "Something went wrong";
    }
    const errorRes = await response.json();
    return errorRes?.message ?? "Something went wrong";
  }

  if (typeof error === "string") {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (
    error &&
    typeof error === "object" &&
    "componentStack" in error &&
    typeof error.componentStack === "string"
  ) {
    return error.componentStack;
  }

  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }

  return "Something went wrong";
};

type ErrorLoggerParams = {
  userInfo?: string | Error | unknown;
  devInfo?: unknown | ErrorInfo;
};

export const logError = async ({ userInfo, devInfo }: ErrorLoggerParams) => {
  const message = await getErrorMessage(userInfo ?? devInfo);

  // Log to console in development
  if (import.meta.env.DEV) {
    console.error(message);
    console.error("Developer Info:", devInfo);
  }

  // Send to Sentry
  Sentry.withScope((scope) => {
    if (devInfo) {
      scope.setExtra("developerInfo", devInfo);
    }

    if (userInfo instanceof Error) {
      Sentry.captureException(userInfo);
    } else if (typeof userInfo === "string") {
      Sentry.captureMessage(userInfo);
    } else {
      Sentry.captureMessage(message);
    }
  });

  return message;
};

export const logAndAlertError = async ({
  userInfo,
  devInfo,
}: ErrorLoggerParams) => {
  CustomAlert.error({
    description: await logError({ userInfo, devInfo }),
    position: "top-right",
  });
};

export const errorBoundaryLogger = (error: Error, info: ErrorInfo) => {
  Sentry.captureReactException(error, info);
  logError({ userInfo: error, devInfo: info });
};
